import { useRef, useState } from "react";
import { Play, Pause, ChevronDown, ChevronUp } from "lucide-react";
import { Reveal } from "./Reveal";
import { WELCOME } from "../data/content";

const fmt = (s) => {
  if (!s || isNaN(s)) return "0:00";
  return `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

export const WelcomePlayer = () => {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [showText, setShowText] = useState(false);

  const toggle = () => {
    const a = audioRef.current;
    if (!a) return;
    if (playing) { a.pause(); } else { a.play(); }
    setPlaying(!playing);
  };

  const onTime = () => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    setCurrent(a.currentTime);
    setProgress((a.currentTime / a.duration) * 100);
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !a.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const pct = (e.clientX - rect.left) / rect.width;
    a.currentTime = pct * a.duration;
  };

  return (
    <section className="bg-[#1E3A2B] px-6 py-24" data-testid="welcome-player-section">
      <div className="max-w-6xl mx-auto grid md:grid-cols-12 gap-10 items-center">
        <Reveal className="md:col-span-4">
          <div className="relative max-w-[320px] mx-auto md:mx-0">
            <div className="absolute -inset-3 bg-gradient-to-br from-[#C77E88] to-[#E7AEB4] -z-0 translate-x-3 translate-y-3" />
            <img src={WELCOME.photo} alt="Coach Nata welcomes you"
              className="relative z-10 w-full aspect-square object-cover" />
            <div className="absolute -bottom-4 -left-4 z-20 bg-[#C0953A] text-[#132619] px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold">
              Welcome Home
            </div>
          </div>
        </Reveal>

        <Reveal delay={0.15} className="md:col-span-8">
          <p className="eyebrow text-[#DCBB6C]">A Personal Welcome</p>
          <h2 className="font-serif-d text-3xl md:text-4xl text-[#FAF4E9] tracking-tight mt-3">
            "You're not here by <span className="italic-emph text-[#E7AEB4]">accident.</span>"
          </h2>
          <p className="text-[#FAF4E9]/65 text-sm mt-3 max-w-xl">
            Press play and let Coach Nata welcome you in — then step into whichever world you need today.
          </p>

          <div className="mt-8 bg-[#132619]/70 border border-[#FAF4E9]/10 p-6 backdrop-blur">
            <div className="flex items-center gap-5">
              <button onClick={toggle} data-testid="welcome-play-btn" aria-label={playing ? "Pause welcome message" : "Play welcome message"}
                className="shrink-0 w-16 h-16 rounded-full bg-[#C0953A] hover:bg-[#DCBB6C] transition-colors flex items-center justify-center text-[#132619]">
                {playing ? <Pause size={26} /> : <Play size={26} className="ml-1" />}
              </button>
              <div className="flex-1">
                <div className="flex justify-between text-xs text-[#FAF4E9]/60 mb-2 uppercase tracking-widest">
                  <span>{playing ? "Now Playing — Welcome to Life With Nata" : "Welcome to Life With Nata"}</span>
                  <span data-testid="welcome-time">{fmt(current)} / {fmt(duration)}</span>
                </div>
                <div onClick={seek} data-testid="welcome-progress-bar"
                  className="h-2 bg-[#FAF4E9]/15 cursor-pointer group relative">
                  <div className="h-full bg-gradient-to-r from-[#C77E88] to-[#DCBB6C] transition-[width] duration-150"
                    style={{ width: `${progress}%` }} />
                </div>
                <div className="flex gap-[3px] items-end h-6 mt-3" aria-hidden="true">
                  {[...Array(36)].map((_, i) => (
                    <span key={i}
                      className={`w-1 bg-[#DCBB6C]/70 ${playing ? "wave-bar" : ""}`}
                      style={{ height: playing ? undefined : "4px", animationDelay: `${(i % 9) * 0.12}s` }} />
                  ))}
                </div>
              </div>
            </div>
            <audio ref={audioRef} src={WELCOME.audio} preload="metadata"
              onTimeUpdate={onTime}
              onLoadedMetadata={(e) => setDuration(e.target.duration)}
              onEnded={() => { setPlaying(false); setProgress(0); setCurrent(0); }} />
          </div>

          <button onClick={() => setShowText(!showText)} data-testid="welcome-transcript-toggle"
            className="mt-4 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#DCBB6C] hover:text-[#FAF4E9] transition-colors">
            {showText ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {showText ? "Hide" : "Read"} the message
          </button>
          {showText && (
            <p data-testid="welcome-transcript" className="mt-4 text-[#FAF4E9]/70 text-sm leading-relaxed max-w-2xl font-sans-d">
              {WELCOME.text}
            </p>
          )}
          <p className="mt-4 text-[10px] uppercase tracking-[0.2em] text-[#FAF4E9]/35">
            A personal message from Coach Nata
          </p>
        </Reveal>
      </div>
    </section>
  );
};
